import {
  navMotionEnabled,
  animateDrawerOpen,
  animateDrawerClose,
  animateSearchOpen,
  animateSearchClose,
} from '@theme/furlouve-nav-motion';
import { ThemeEvents } from '@theme/events';

/**
 * Toggles the scroll-locked body class and notifies the premium motion
 * layer (see assets/premium-motion.js) so it can stop/start Lenis in sync —
 * keeps every overlay's scroll-lock in one place rather than each one
 * needing to know Lenis exists.
 * @param {boolean} locked
 */
function setScrollLocked(locked) {
  document.body.classList.toggle('flv-scroll-locked', locked);
  document.dispatchEvent(new CustomEvent('flv:scroll-lock-change', { detail: { locked } }));
}

const navBurgerCheckbox = /** @type {HTMLInputElement | null} */ (document.querySelector('.flv-hamburger__checkbox'));
const menuOverlay = document.querySelector('[data-flv-menu-overlay]');
const mobileMenu = document.querySelector('[data-flv-mobile-menu]');

if (navBurgerCheckbox && mobileMenu && menuOverlay) {
  const openMenu = () => {
    navBurgerCheckbox.checked = true;
    mobileMenu.setAttribute('aria-hidden', 'false');
    setScrollLocked(true);

    if (navMotionEnabled) {
      mobileMenu.classList.add('is-open');
      menuOverlay.classList.add('is-open');
      animateDrawerOpen(menuOverlay, mobileMenu);
    } else {
      mobileMenu.classList.add('is-open');
      menuOverlay.classList.add('is-open');
    }
  };

  const closeMenu = () => {
    navBurgerCheckbox.checked = false;
    mobileMenu.setAttribute('aria-hidden', 'true');
    setScrollLocked(false);

    if (navMotionEnabled) {
      animateDrawerClose(menuOverlay, mobileMenu, () => {
        mobileMenu.classList.remove('is-open');
        menuOverlay.classList.remove('is-open');
      });
    } else {
      mobileMenu.classList.remove('is-open');
      menuOverlay.classList.remove('is-open');
    }
  };

  navBurgerCheckbox.addEventListener('change', () => {
    navBurgerCheckbox.checked ? openMenu() : closeMenu();
  });
  menuOverlay.addEventListener('click', closeMenu);
  mobileMenu.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && mobileMenu.classList.contains('is-open')) closeMenu();
  });
}

const searchTrigger = document.querySelector('[data-search-trigger]');
const searchModal = document.querySelector('[data-flv-search-modal]');
const searchPanel = document.querySelector('.flv-search-modal__panel');
const searchInput = /** @type {HTMLInputElement | null} */ (document.querySelector('[data-flv-search-input]'));
const searchClose = document.querySelector('[data-flv-search-close]');

if (searchTrigger && searchModal && searchPanel && searchInput && searchClose) {
  const openSearch = () => {
    searchModal.setAttribute('aria-hidden', 'false');
    setScrollLocked(true);
    searchModal.classList.add('is-open');

    if (navMotionEnabled) {
      animateSearchOpen(searchModal, searchPanel);
    }

    // Double rAF: focusing a button natively refocuses it as its default
    // click action, which runs after our handler — and the modal needs a
    // frame to leave display:none before it can receive focus at all.
    requestAnimationFrame(() => requestAnimationFrame(() => searchInput?.focus()));
  };

  const closeSearch = () => {
    searchModal.setAttribute('aria-hidden', 'true');
    setScrollLocked(false);

    if (navMotionEnabled) {
      animateSearchClose(searchModal, searchPanel, () => searchModal.classList.remove('is-open'));
    } else {
      searchModal.classList.remove('is-open');
    }
  };

  searchTrigger.addEventListener('click', openSearch);
  searchClose.addEventListener('click', closeSearch);
  searchModal.addEventListener('click', (event) => {
    if (event.target === searchModal) closeSearch();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && searchModal.classList.contains('is-open')) closeSearch();
  });
}

// Keeps the header's cart badge in sync with AJAX cart changes (e.g. a
// product-card add-to-cart) without needing a page reload. Reuses the
// theme's own cart:update event rather than a bespoke one, so anything
// else that dispatches it — now or in the future — updates this too.
const cartBadge = document.querySelector('[data-flv-cart-badge]');
if (cartBadge) {
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const itemCount = /** @type {CustomEvent} */ (event).detail?.data?.itemCount;
    if (typeof itemCount !== 'number') return;
    cartBadge.textContent = String(itemCount);
    cartBadge.classList.toggle('flv-icon-dock__badge--visible', itemCount > 0);
  });
}

// Header cart icon opens the Furlouve cart drawer (snippets/furlouve-cart-drawer.liquid,
// rendered once by this section) instead of navigating to /cart. Left as a
// real <a href="{{ routes.cart_url }}"> in the markup so it still works
// as a plain link if this script fails to load or the drawer's own
// custom element isn't defined yet for any reason.
const cartDrawerTrigger = document.querySelector('[data-flv-open-cart-drawer]');
if (cartDrawerTrigger) {
  cartDrawerTrigger.addEventListener('click', (event) => {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
    event.preventDefault();
    cartDrawer.open();
  });
}
